import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './api';
import { getAccessToken } from './authSession';
import Spinner from './Spinner';

const REPORTS = [
    { id: 'pending_tasks', label: 'Pending tasks', needsRange: false },
    { id: 'roster', label: 'Employee roster', needsRange: false },
    { id: 'daily', label: 'Daily manning', needsRange: false },
    { id: 'absences', label: 'Absence board', needsRange: false },
    { id: 'leave_calendar', label: 'Leave calendar', needsRange: true },
    { id: 'fill_rate', label: 'Fill / coverage rate', needsRange: false },
    { id: 'vacancies', label: 'Vacancies', needsRange: false },
    { id: 'leave_impact', label: 'Leave impact', needsRange: true },
    { id: 'audit', label: 'Audit / change log', needsRange: true },
];

function asArray(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.rows)) return data.rows;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
}

function formatError(err) {
    const data = err?.response?.data;
    if (!data) return err?.message || 'Request failed';
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.error) return data.error;
    return 'Request failed';
}

function defaultStart() {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
}

function defaultEnd() {
    return new Date().toISOString().slice(0, 10);
}

function getStyles(theme) {
    const isDark = theme === 'dark';
    return {
        hint: { color: isDark ? '#9aa6b2' : '#6c757d', fontSize: '13px', margin: '0 0 14px' },
        filters: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            alignItems: 'end',
            marginBottom: '14px',
        },
        formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
        label: { fontWeight: 600, fontSize: '13px' },
        input: {
            padding: '10px',
            borderRadius: '4px',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: isDark ? '#e0e0e0' : '#212529',
            fontSize: '14px',
            minWidth: '140px',
        },
        button: {
            padding: '10px 14px',
            backgroundColor: isDark ? '#6cb2f7' : '#0d6efd',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        secondaryButton: {
            padding: '10px 14px',
            backgroundColor: 'transparent',
            color: isDark ? '#e0e0e0' : '#212529',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        summaryBar: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '10px',
            marginBottom: '14px',
        },
        summaryCard: {
            backgroundColor: isDark ? '#252525' : '#f8f9fa',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '8px',
            padding: '12px',
            textAlign: 'center',
        },
        summaryNumber: { fontSize: '1.3rem', fontWeight: 700 },
        summaryLabel: { fontSize: '0.8rem', color: isDark ? '#9aa6b2' : '#6c757d' },
        tableWrap: { overflowX: 'auto' },
        table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
        th: {
            textAlign: 'left',
            padding: '8px',
            borderBottom: `2px solid ${isDark ? '#444' : '#dee2e6'}`,
            whiteSpace: 'nowrap',
        },
        td: {
            padding: '8px',
            borderBottom: `1px solid ${isDark ? '#333' : '#dee2e6'}`,
            verticalAlign: 'top',
        },
        empty: {
            padding: '24px',
            textAlign: 'center',
            color: isDark ? '#9aa6b2' : '#6c757d',
        },
        badge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 700,
            backgroundColor: isDark ? '#333' : '#e9ecef',
        },
        warn: { color: isDark ? '#ffc107' : '#996c00', fontWeight: 700 },
    };
}

function columnsFor(reportId) {
    switch (reportId) {
        case 'pending_tasks':
            return [
                ['request_number', 'Request'],
                ['employee_name', 'Employee'],
                ['leave_type', 'Type'],
                ['status', 'Status'],
                ['start', 'Start'],
                ['end', 'End'],
                ['total_hours', 'Hours'],
            ];
        case 'roster':
            return [
                ['employee_number', 'Emp #'],
                ['display_name', 'Name'],
                ['job_title', 'Title'],
                ['site', 'Site'],
                ['work_center', 'Work center'],
                ['shift', 'Shift'],
                ['status', 'Status'],
                ['telephone', 'Phone'],
            ];
        case 'daily':
        case 'fill_rate':
            return [
                ['site', 'Site'],
                ['work_center_code', 'WC'],
                ['work_center_name', 'Name'],
                ['required', 'Required'],
                ['assigned', 'Assigned'],
                ['available', 'Available'],
                ['absent', 'Absent'],
                ['temporary_in', 'Temp in'],
                ['temporary_out', 'Temp out'],
                ['vacancies', 'Vacant'],
                ['fill_rate', 'Fill %'],
                ['coverage_rate', 'Cover %'],
                ['below_minimum', 'Below min'],
            ];
        case 'absences':
            return [
                ['display_name', 'Name'],
                ['employee_number', 'Emp #'],
                ['work_center', 'WC'],
                ['badge', 'Status'],
                ['leave_type', 'Type'],
                ['start', 'Start'],
                ['return_date', 'Return'],
                ['source', 'Source'],
            ];
        case 'leave_calendar':
            return [
                ['request_number', 'Request'],
                ['display_name', 'Name'],
                ['leave_type', 'Type'],
                ['status', 'Status'],
                ['start', 'Start'],
                ['end', 'End'],
                ['total_hours', 'Hours'],
                ['work_center', 'WC'],
            ];
        case 'vacancies':
            return [
                ['site', 'Site'],
                ['work_center', 'Work center'],
                ['position', 'Position'],
                ['required', 'Required'],
                ['filled', 'Filled'],
                ['vacancies', 'Vacancies'],
                ['is_funded', 'Funded'],
            ];
        case 'leave_impact':
            return [
                ['request_number', 'Request'],
                ['display_name', 'Name'],
                ['leave_type', 'Type'],
                ['work_center', 'WC'],
                ['total_hours', 'Lost hours'],
                ['start', 'Start'],
                ['end', 'End'],
            ];
        case 'audit':
            return [
                ['timestamp', 'When'],
                ['user', 'User'],
                ['action', 'Action'],
                ['entity_type', 'Entity'],
                ['summary', 'Summary'],
            ];
        default:
            return [];
    }
}

function formatCell(key, value) {
    if (value == null || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (key.includes('start') || key.includes('end') || key === 'timestamp' || key === 'submitted_at') {
        const d = new Date(value);
        if (!Number.isNaN(d.getTime())) return d.toLocaleString();
    }
    return String(value);
}

function ManningReportsPanel({ theme, sites, onNotify }) {
    const styles = useMemo(() => getStyles(theme), [theme]);
    const [reportId, setReportId] = useState('pending_tasks');
    const [loading, setLoading] = useState(false);
    const [payload, setPayload] = useState(null);
    const [filters, setFilters] = useState({
        site: '',
        as_of: defaultEnd(),
        start: defaultStart(),
        end: defaultEnd(),
        status: '',
        search: '',
        open_only: true,
    });

    const selectedMeta = REPORTS.find((r) => r.id === reportId) || REPORTS[0];

    const loadReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.site) params.site = filters.site;
            if (filters.search) params.search = filters.search;
            if (filters.status) params.status = filters.status;
            if (['daily', 'absences', 'fill_rate', 'vacancies', 'roster'].includes(reportId)) {
                if (filters.as_of) params.as_of = filters.as_of;
            }
            if (selectedMeta.needsRange) {
                if (filters.start) params.start = filters.start;
                if (filters.end) params.end = filters.end;
            }
            const res = await api.get(`/manning-reports/${reportId}/`, { params });
            setPayload(res.data);
        } catch (err) {
            onNotify?.(formatError(err), 'error');
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [filters, reportId, selectedMeta.needsRange, onNotify]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const rows = useMemo(() => {
        if (!payload) return [];
        if (reportId === 'vacancies' && filters.open_only) {
            return payload.open_rows || asArray(payload).filter((r) => r.vacancies > 0);
        }
        if (reportId === 'fill_rate') {
            return (payload.rows || []).map((r) => ({
                ...r,
                fill_rate: r.manning_fill_rate ?? r.fill_rate,
                coverage_rate: r.operational_coverage ?? r.coverage_rate,
            }));
        }
        return asArray(payload);
    }, [payload, reportId, filters.open_only]);

    const columns = columnsFor(reportId);

    const exportExcel = async () => {
        try {
            const params = new URLSearchParams({ export: 'xlsx' });
            if (filters.site) params.set('site', filters.site);
            if (filters.search) params.set('search', filters.search);
            if (filters.status) params.set('status', filters.status);
            if (filters.as_of) params.set('as_of', filters.as_of);
            if (filters.start) params.set('start', filters.start);
            if (filters.end) params.set('end', filters.end);

            const token = getAccessToken();
            const base = (api.defaults.baseURL || '').replace(/\/+$/, '');
            const url = `${base}/manning-reports/${reportId}/?${params.toString()}`;
            const response = await fetch(url, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Export failed');
            }
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `manning_${reportId}_${filters.as_of || defaultEnd()}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            onNotify?.('Excel export downloaded');
        } catch (err) {
            onNotify?.(err.message || 'Export failed', 'error');
        }
    };

    const summaryCards = [];
    if (payload) {
        if (reportId === 'pending_tasks') {
            summaryCards.push(['Pending approvals', payload.count ?? rows.length]);
        }
        if (reportId === 'roster') {
            summaryCards.push(['Employees', payload.count ?? rows.length]);
        }
        if (payload.totals) {
            summaryCards.push(
                ['Available', payload.totals.available],
                ['Absent', payload.totals.absent],
                ['Vacancies', payload.totals.vacancies],
                ['Below minimum', payload.totals.below_minimum],
            );
        }
        if (reportId === 'leave_impact') {
            summaryCards.push(
                ['Leave chits', payload.leave_count],
                ['Lost hours', payload.total_lost_hours],
            );
        }
        if (reportId === 'vacancies') {
            summaryCards.push(
                ['Positions', payload.count],
                ['Open vacancies', payload.open_vacancies],
            );
        }
        if (reportId === 'absences' || reportId === 'leave_calendar' || reportId === 'audit') {
            summaryCards.push(['Rows', payload.count ?? rows.length]);
        }
    }

    return (
        <div>
            <p style={styles.hint}>
                Leadership reports, pending approval tasks, leave calendar, and Excel exports.
                Utilization vs Costpoint remains a later phase.
            </p>

            <div style={styles.filters}>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Report</label>
                    <select
                        style={styles.input}
                        value={reportId}
                        onChange={(e) => setReportId(e.target.value)}
                    >
                        {REPORTS.map((r) => (
                            <option key={r.id} value={r.id}>
                                {r.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Site</label>
                    <select
                        style={styles.input}
                        value={filters.site}
                        onChange={(e) =>
                            setFilters((prev) => ({ ...prev, site: e.target.value }))
                        }
                    >
                        <option value="">All sites</option>
                        {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                                {site.name}
                            </option>
                        ))}
                    </select>
                </div>
                {!selectedMeta.needsRange && reportId !== 'pending_tasks' && (
                    <div style={styles.formGroup}>
                        <label style={styles.label}>As of</label>
                        <input
                            style={styles.input}
                            type="date"
                            value={filters.as_of}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, as_of: e.target.value }))
                            }
                        />
                    </div>
                )}
                {selectedMeta.needsRange && (
                    <>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Start</label>
                            <input
                                style={styles.input}
                                type="date"
                                value={filters.start}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, start: e.target.value }))
                                }
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>End</label>
                            <input
                                style={styles.input}
                                type="date"
                                value={filters.end}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, end: e.target.value }))
                                }
                            />
                        </div>
                    </>
                )}
                {reportId === 'roster' && (
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Status</label>
                        <select
                            style={styles.input}
                            value={filters.status}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, status: e.target.value }))
                            }
                        >
                            <option value="">Active family</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                            <option value="On Extended LOA">On Extended LOA</option>
                            <option value="Pending Start">Pending Start</option>
                            <option value="Separated">Separated</option>
                        </select>
                    </div>
                )}
                {reportId === 'vacancies' && (
                    <div style={{ ...styles.formGroup, justifyContent: 'center' }}>
                        <label style={styles.label}>
                            <input
                                type="checkbox"
                                checked={filters.open_only}
                                onChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        open_only: e.target.checked,
                                    }))
                                }
                            />{' '}
                            Open vacancies only
                        </label>
                    </div>
                )}
                <button type="button" style={styles.button} onClick={loadReport}>
                    Run report
                </button>
                {reportId !== 'pending_tasks' && (
                    <button type="button" style={styles.secondaryButton} onClick={exportExcel}>
                        Export Excel
                    </button>
                )}
            </div>

            {summaryCards.length > 0 && (
                <div style={styles.summaryBar}>
                    {summaryCards.map(([label, value]) => (
                        <div key={label} style={styles.summaryCard}>
                            <div style={styles.summaryNumber}>{value ?? '—'}</div>
                            <div style={styles.summaryLabel}>{label}</div>
                        </div>
                    ))}
                </div>
            )}

            {reportId === 'leave_impact' && payload?.by_work_center?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                    <strong>Lost hours by work center</strong>
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Site</th>
                                    <th style={styles.th}>WC</th>
                                    <th style={styles.th}>Leave count</th>
                                    <th style={styles.th}>Lost hours</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payload.by_work_center.map((row) => (
                                    <tr key={row.work_center_code}>
                                        <td style={styles.td}>{row.site || '—'}</td>
                                        <td style={styles.td}>
                                            {row.work_center_code} — {row.work_center_name}
                                        </td>
                                        <td style={styles.td}>{row.leave_count}</td>
                                        <td style={styles.td}>{row.lost_hours}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {loading ? (
                <div style={styles.empty}>
                    <Spinner />
                </div>
            ) : (
                <div style={styles.tableWrap}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                {columns.map(([key, label]) => (
                                    <th key={key} style={styles.th}>
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td style={styles.td} colSpan={columns.length}>
                                        No rows for this report / filter set.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row, index) => (
                                    <tr key={row.id || row.request_number || row.employee_number || index}>
                                        {columns.map(([key]) => (
                                            <td key={key} style={styles.td}>
                                                {key === 'below_minimum' && row[key] ? (
                                                    <span style={styles.warn}>Yes</span>
                                                ) : key === 'status' || key === 'badge' || key === 'action' ? (
                                                    <span style={styles.badge}>
                                                        {formatCell(key, row[key])}
                                                    </span>
                                                ) : (
                                                    formatCell(key, row[key])
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default ManningReportsPanel;
